﻿import React, { useState, useEffect } from 'react';
import { DatabaseHistoryRow } from '../models/Auditing/database-history-row';
import { BenchSheetTechnologyType, Entities } from '../models/enums';
import { AuditHistoryRequest } from '../models/audit-history-request';
import { BenchSheetAuditHistoryRequest } from '../models/bench-sheet-audit-history-request';
import { CrudFunctions } from '../utilities/crud-functions';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';

import DataGrid, {
    FilterRow,
    HeaderFilter,
    SearchPanel,
    GroupPanel,
    Export,
    Column,
    Selection,
    Scrolling,
    Paging,
    LoadPanel,
    Toolbar,
    Item as ToolbarItem,
    FilterPanel,
    FilterBuilderPopup,
    Position
} from 'devextreme-react/data-grid';

import { saveAs } from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';

export interface Props {
    entityNumber?: Entities;
    rowPointer?: string;
    childData?: boolean;
    technology?: BenchSheetTechnologyType;
    showOtherIdColumn?: boolean;
}

export default function AuditHistoryComponent(props: Props) {
    const [auditItems, setAuditItems] =
        useState<DatabaseHistoryRow[]>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getAllData()
    }, []);

    return (
        <div className="pw-content">
            <div className="pw-boxed-form-section">
                <div>
                    <DataGrid
                        height={'43rem'}
                        wordWrapEnabled={true}
                        dataSource={auditItems ? auditItems : []}
                        showBorders={true}
                        allowColumnResizing={true}
                        onExporting={onExporting}
                        columnResizingMode='nextColumn'>

                        <LoadPanel enabled={true} />
                        <Selection mode={'single'} />
                        <Scrolling showScrollbar='always' />
                        <Paging enabled={false} />
                        <HeaderFilter visible={true} />
                        <FilterRow visible={true} />
                        
                        <GroupPanel visible={true} />
                        <FilterPanel visible={true} />
                        <FilterBuilderPopup>
                            <Position of={window} at='center' my='center' />
                        </FilterBuilderPopup>

                        <SearchPanel visible={true}
                            width={250}
                            placeholder="Search..." />
                        <Export enabled={true} allowExportSelectedData={true} />

                        {props.technology &&
                            <Column dataField={'benchSheetEntityName'} width={'10%'} groupIndex={0} />
                        }
                        <Column dataField={'fieldName'} dataType="number" width={'15%'} alignment='left' />

                        {props.showOtherIdColumn &&
                            <Column dataField={'otherId'} width="15%" />
                        }

                        <Column dataField={'operation'} width={'12%'} />
                        <Column dataField={'modifyTimestamp'} width={'21%'} dataType="date" format="MM/dd/yyyy HH:mm:ss" />
                        <Column dataField={'modifyUser'} width={'12%'} />

                        <Column dataField={'value'} width={'40%'} />

                        <Toolbar>
                            <ToolbarItem location='before'>
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">Audit Items{props.entityNumber ? ` - ${Entities[props.entityNumber]}` : ''}</div>
                                </div>
                            </ToolbarItem>
                            <ToolbarItem name="groupPanel" location='after' />
                            <ToolbarItem name="exportButton" location='after' />
                            <ToolbarItem name="searchPanel" location='after' />
                        </Toolbar>

                    </DataGrid>
                </div>
            </div>
            <div className="pw-form-unit-label">* Lookup value</div>

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} onClose={clearGlobalDialog} />
            }
        </div>
    );

    //exporting

    function onExporting(e: any) {
        const name = "DMA Audit Log";

        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet('Main sheet');

        exportDataGrid({
            component: e.component,
            worksheet,
            autoFilterEnabled: true,
        }).then(() => {
            workbook.xlsx.writeBuffer().then((buffer) => {
                saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${name}.xlsx`);
            });
        });

        e.cancel = true;
    }

    async function getAuditHistory() {

        console.log("In getAuditHistory()");

        let request: AuditHistoryRequest = {
            entityNumber: props.entityNumber
        };

        if (props.technology) {
            getBenchSheetAuditInformation();
        }
        else if (props.childData && props.rowPointer) {
            request.parentRowPointer = props.rowPointer;

            await getProductChildAuditHistory(request)
                .then((data) => {
                    setAuditItems(data);
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving Audit Data");
                });
        }
        else {
            request.rowPointer = props.rowPointer;

            await getEntityAuditHistoryByRowPointer(request)
                .then((data) => {
                    console.log(data);

                    setAuditItems(data);
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving Audit Data");
                });
        }
    }

    async function getBenchSheetAuditInformation() {
        const request: BenchSheetAuditHistoryRequest = {
            rowPointer: props.rowPointer,
            technology: props.technology
        };

        await getBenchSheetAuditData(request)
            .then((data) => {
                setAuditItems(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Audit Data");
            });
    }

    //GET functions

    async function getEntityAuditHistoryByRowPointer(request: AuditHistoryRequest) {
        return await CrudFunctions.postData("auditing/historybyrowpointer", request);
    }

    async function getProductChildAuditHistory(request: AuditHistoryRequest) {
        return await CrudFunctions.postData("auditing/productchildhistory", request);
    }

    async function getBenchSheetAuditData(request: BenchSheetAuditHistoryRequest) {
        return CrudFunctions.postData("auditing/benchsheethistory", request);
    }

    async function getAllData() {
        await getAuditHistory();
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }
}
